// ============================================================
// AudioAnalyzer.js — Core audio analysis pipeline
// Detects BPM, musical key, energy profile, and spectral
// characteristics from an uploaded audio loop.
// 100% client-side via Web Audio API. No external dependencies.
//
// Uses Cooley-Tukey radix-2 FFT for O(n log n) performance
// instead of naive DFT — critical for browser main thread.
// ============================================================

// --- Note / pitch constants ---
const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// Krumhansl-Kessler key profiles — perceptual weightings for major and minor keys
const MAJOR_PROFILE = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const MINOR_PROFILE = [6.33, 2.68, 3.52, 5.38, 2.60, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

// Valid bar counts for musical loops
const VALID_BAR_COUNTS = [4, 8, 16, 32];


// ============================================================
// Cooley-Tukey Radix-2 FFT — O(n log n) in-place
// ============================================================

/**
 * In-place radix-2 FFT. Modifies real[] and imag[] arrays.
 * n must be a power of 2.
 */
function fft(real, imag, n) {
  // Bit-reversal permutation
  let j = 0;
  for (let i = 0; i < n - 1; i++) {
    if (i < j) {
      let tmp = real[i]; real[i] = real[j]; real[j] = tmp;
      tmp = imag[i]; imag[i] = imag[j]; imag[j] = tmp;
    }
    let k = n >> 1;
    while (k <= j) { j -= k; k >>= 1; }
    j += k;
  }

  // Butterfly stages
  for (let len = 2; len <= n; len *= 2) {
    const halfLen = len / 2;
    const angle = -2 * Math.PI / len;
    const wReal = Math.cos(angle);
    const wImag = Math.sin(angle);

    for (let i = 0; i < n; i += len) {
      let curReal = 1, curImag = 0;
      for (let k = 0; k < halfLen; k++) {
        const idx1 = i + k;
        const idx2 = i + k + halfLen;
        const tReal = curReal * real[idx2] - curImag * imag[idx2];
        const tImag = curReal * imag[idx2] + curImag * real[idx2];
        real[idx2] = real[idx1] - tReal;
        imag[idx2] = imag[idx1] - tImag;
        real[idx1] += tReal;
        imag[idx1] += tImag;
        const newCurReal = curReal * wReal - curImag * wImag;
        curImag = curReal * wImag + curImag * wReal;
        curReal = newCurReal;
      }
    }
  }
}

/**
 * Compute magnitude spectrum from a windowed frame.
 * Returns Float64Array of length fftSize/2 with magnitudes normalized by fftSize.
 */
function computeSpectrum(frame, fftSize) {
  const real = new Float64Array(fftSize);
  const imag = new Float64Array(fftSize);
  for (let i = 0; i < fftSize; i++) real[i] = frame[i] || 0;

  fft(real, imag, fftSize);

  const spectrum = new Float64Array(fftSize / 2);
  for (let i = 0; i < fftSize / 2; i++) {
    spectrum[i] = Math.sqrt(real[i] * real[i] + imag[i] * imag[i]) / fftSize;
  }
  return spectrum;
}

/**
 * Apply Hann window to a frame of audio data.
 */
function hannWindow(data, offset, fftSize) {
  const frame = new Float64Array(fftSize);
  for (let i = 0; i < fftSize; i++) {
    const hann = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (fftSize - 1)));
    frame[i] = (data[offset + i] || 0) * hann;
  }
  return frame;
}


// --- BPM Detection -----------------------------------------------------------

/**
 * Loop-aware BPM detection.
 *
 * Primary strategy: Since loops are always 4, 8, 16, or 32 bars,
 * we compute the BPM each bar count implies from the file duration.
 * The candidate that rounds cleanest to a whole number wins.
 *
 * Secondary: onset-based peak detection as tiebreaker.
 */
export async function detectBPM(audioBuffer) {
  const sampleRate = audioBuffer.sampleRate;
  const duration = audioBuffer.duration;

  // --- Strategy 1: Duration-based BPM (primary for loops) ---
  const durationCandidates = [];
  for (const bars of VALID_BAR_COUNTS) {
    const bpm = (bars * 4 * 60) / duration;
    if (bpm >= 60 && bpm <= 200) {
      const rounded = Math.round(bpm);
      const drift = Math.abs(bpm - rounded);
      durationCandidates.push({ bars, bpm: rounded, rawBpm: bpm, drift });
    }
  }
  durationCandidates.sort((a, b) => a.drift - b.drift);

  // --- Strategy 2: Onset detection (secondary/tiebreaker) ---
  const onsetBpm = await detectBPMFromOnsets(audioBuffer);

  // --- Consensus ---
  let bestCandidate = durationCandidates[0];
  let confidence = 0;

  if (bestCandidate) {
    for (const c of durationCandidates) {
      if (c === bestCandidate) continue;
      const ratio = c.bpm / bestCandidate.bpm;
      if (Math.abs(ratio - 2) < 0.01 || Math.abs(ratio - 0.5) < 0.01) {
        if (onsetBpm > 0) {
          const distBest = Math.abs(onsetBpm - bestCandidate.bpm) / bestCandidate.bpm;
          const distAlt = Math.abs(onsetBpm - c.bpm) / c.bpm;
          if (distAlt < distBest) bestCandidate = c;
        } else {
          const prefer8 = durationCandidates.find(x => x.bars === 8);
          if (prefer8) bestCandidate = prefer8;
        }
        break;
      }
    }

    confidence = Math.max(0, 1 - bestCandidate.drift * 4);
    if (onsetBpm > 0 && Math.abs(onsetBpm - bestCandidate.bpm) / bestCandidate.bpm < 0.10) {
      confidence = Math.min(1, confidence + 0.15);
    }
  } else {
    const fallbackBpm = onsetBpm > 0 ? onsetBpm : 120;
    const bars = Math.max(1, Math.round((duration / 60) * fallbackBpm / 4));
    return { bpm: fallbackBpm, bars, confidence: onsetBpm > 0 ? 0.5 : 0, alternatives: [] };
  }

  const alternatives = durationCandidates
    .filter(c => c.bpm !== bestCandidate.bpm)
    .map(c => c.bpm);

  return {
    bpm: bestCandidate.bpm,
    bars: bestCandidate.bars,
    confidence: Math.round(confidence * 100) / 100,
    alternatives
  };
}

/**
 * Onset-based BPM estimation (helper).
 * Uses lowpass filtering + peak detection + interval clustering.
 */
async function detectBPMFromOnsets(audioBuffer) {
  try {
  const sampleRate = audioBuffer.sampleRate;
  const maxSamples = Math.min(audioBuffer.length, sampleRate * 30);

  const offlineCtx = new OfflineAudioContext(1, maxSamples, sampleRate);
  const source = offlineCtx.createBufferSource();
  source.buffer = audioBuffer;

  const lowpass = offlineCtx.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.value = 150;
  lowpass.Q.value = 1;

  source.connect(lowpass);
  lowpass.connect(offlineCtx.destination);
  source.start(0);

  const filtered = await offlineCtx.startRendering();
  const data = filtered.getChannelData(0);

  let maxVal = 0;
  for (let i = 0; i < data.length; i++) {
    const abs = Math.abs(data[i]);
    if (abs > maxVal) maxVal = abs;
  }
  if (maxVal === 0) return 0;

  const threshold = maxVal * 0.4;
  const minPeakDistance = Math.floor((60 / 200) * sampleRate);
  const peaks = [];
  let lastPeak = -minPeakDistance;

  for (let i = 1; i < data.length - 1; i++) {
    const val = Math.abs(data[i]);
    if (val > threshold &&
        val > Math.abs(data[i - 1]) &&
        val > Math.abs(data[i + 1]) &&
        (i - lastPeak) >= minPeakDistance) {
      peaks.push(i);
      lastPeak = i;
    }
  }

  if (peaks.length < 2) return 0;

  const intervals = [];
  for (let i = 1; i < peaks.length; i++) {
    intervals.push(peaks[i] - peaks[i - 1]);
  }

  const buckets = [];
  for (const interval of intervals) {
    let placed = false;
    for (const bucket of buckets) {
      if (Math.abs(interval - bucket.center) / bucket.center < 0.08) {
        bucket.intervals.push(interval);
        bucket.center = bucket.intervals.reduce((a, b) => a + b, 0) / bucket.intervals.length;
        placed = true;
        break;
      }
    }
    if (!placed) {
      buckets.push({ center: interval, intervals: [interval] });
    }
  }

  buckets.sort((a, b) => b.intervals.length - a.intervals.length);
  if (buckets.length === 0) return 0;

  let bpm = (60 * sampleRate) / buckets[0].center;
  while (bpm > 200) bpm /= 2;
  while (bpm < 60) bpm *= 2;

  return Math.round(bpm);
  } catch (e) {
    console.warn('[AudioAnalyzer] Onset BPM detection failed, using duration-only:', e.message);
    return 0;
  }
}


// --- Key Detection (Chromagram-based, using FFT) -----------------------------

/**
 * Detects the musical key using chroma analysis with proper FFT.
 */
export async function detectKey(audioBuffer) {
  const sampleRate = audioBuffer.sampleRate;
  const channelData = audioBuffer.getChannelData(0);
  const maxSamples = Math.min(channelData.length, sampleRate * 30);
  const data = channelData.slice(0, maxSamples);

  const fftSize = 4096;
  const hopSize = 2048;
  const numWindows = Math.floor((data.length - fftSize) / hopSize);

  if (numWindows < 1) {
    return { key: 'C', mode: 'Major', keyString: 'C Major', confidence: 0, chroma: new Array(12).fill(0) };
  }

  const freqResolution = sampleRate / fftSize;
  const chromaAccum = new Float64Array(12);

  // Pre-compute which FFT bin maps to which pitch class
  // This avoids per-bin Goertzel and uses the full FFT instead
  const binToPitchClass = new Int8Array(fftSize / 2).fill(-1);
  for (let pitchClass = 0; pitchClass < 12; pitchClass++) {
    for (let octave = 2; octave <= 7; octave++) {
      const midiNote = 12 * (octave + 1) + pitchClass;
      const freq = 440 * Math.pow(2, (midiNote - 69) / 12);
      if (freq > sampleRate / 2) continue;
      const bin = Math.round(freq / freqResolution);
      if (bin < fftSize / 2) {
        binToPitchClass[bin] = pitchClass;
        // Also capture adjacent bins for smearing
        if (bin > 0 && binToPitchClass[bin - 1] === -1) binToPitchClass[bin - 1] = pitchClass;
        if (bin + 1 < fftSize / 2 && binToPitchClass[bin + 1] === -1) binToPitchClass[bin + 1] = pitchClass;
      }
    }
  }

  for (let w = 0; w < numWindows; w++) {
    const frame = hannWindow(data, w * hopSize, fftSize);
    const spectrum = computeSpectrum(frame, fftSize);

    // Accumulate into chroma bins
    for (let bin = 0; bin < fftSize / 2; bin++) {
      const pc = binToPitchClass[bin];
      if (pc >= 0) chromaAccum[pc] += spectrum[bin];
    }
  }

  // Normalize chroma vector
  const chromaMax = Math.max(...chromaAccum);
  const chroma = Array.from(chromaAccum).map(v => chromaMax > 0 ? v / chromaMax : 0);

  // Correlate against all 24 key profiles
  let bestKey = 0, bestMode = 'Major', bestCorr = -Infinity;

  const correlate = (a, b) => {
    const meanA = a.reduce((s, v) => s + v, 0) / a.length;
    const meanB = b.reduce((s, v) => s + v, 0) / b.length;
    let num = 0, denA = 0, denB = 0;
    for (let i = 0; i < a.length; i++) {
      const da = a[i] - meanA, db = b[i] - meanB;
      num += da * db; denA += da * da; denB += db * db;
    }
    return denA > 0 && denB > 0 ? num / Math.sqrt(denA * denB) : 0;
  };

  for (let shift = 0; shift < 12; shift++) {
    const rotated = [...chroma.slice(shift), ...chroma.slice(0, shift)];
    const majorCorr = correlate(rotated, MAJOR_PROFILE);
    const minorCorr = correlate(rotated, MINOR_PROFILE);

    if (majorCorr > bestCorr) { bestCorr = majorCorr; bestKey = shift; bestMode = 'Major'; }
    if (minorCorr > bestCorr) { bestCorr = minorCorr; bestKey = shift; bestMode = 'Minor'; }
  }

  const confidence = Math.max(0, Math.min(1, (bestCorr + 1) / 2));

  return {
    key: NOTE_NAMES[bestKey],
    mode: bestMode,
    keyString: `${NOTE_NAMES[bestKey]} ${bestMode}`,
    confidence: Math.round(confidence * 100) / 100,
    chroma
  };
}


// --- Energy Profile (using FFT) ----------------------------------------------

/**
 * Analyzes spectral energy distribution using proper FFT.
 */
export async function analyzeEnergy(audioBuffer) {
  const sampleRate = audioBuffer.sampleRate;
  const channelData = audioBuffer.getChannelData(0);
  const maxSamples = Math.min(channelData.length, sampleRate * 30);
  const data = channelData.slice(0, maxSamples);

  const fftSize = 2048;
  const hopSize = 1024;
  const numWindows = Math.floor((data.length - fftSize) / hopSize);

  if (numWindows < 1) {
    return { bass: 0, mid: 0, treble: 0, percussive: 0, melodic: 0, overall: 0, density: 'unknown' };
  }

  const freqResolution = sampleRate / fftSize;
  const bassBinMax = Math.floor(250 / freqResolution);
  const midBinMax = Math.floor(4000 / freqResolution);

  let bassTotal = 0, midTotal = 0, trebleTotal = 0;
  let spectralFluxTotal = 0;
  let spectralCentroidTotal = 0;
  let prevSpectrum = null;

  for (let w = 0; w < numWindows; w++) {
    const frame = hannWindow(data, w * hopSize, fftSize);
    const spectrum = computeSpectrum(frame, fftSize);

    // Band energies (RMS)
    let bassSum = 0, midSum = 0, trebleSum = 0, totalMag = 0;
    for (let bin = 0; bin < fftSize / 2; bin++) {
      const mag2 = spectrum[bin] * spectrum[bin];
      totalMag += spectrum[bin];
      if (bin <= bassBinMax) bassSum += mag2;
      else if (bin <= midBinMax) midSum += mag2;
      else trebleSum += mag2;
    }

    bassTotal += Math.sqrt(bassSum / Math.max(1, bassBinMax));
    midTotal += Math.sqrt(midSum / Math.max(1, midBinMax - bassBinMax));
    trebleTotal += Math.sqrt(trebleSum / Math.max(1, fftSize / 2 - midBinMax));

    // Spectral centroid
    if (totalMag > 0) {
      let centroid = 0;
      for (let bin = 0; bin < fftSize / 2; bin++) centroid += bin * spectrum[bin];
      spectralCentroidTotal += (centroid / totalMag) * freqResolution;
    }

    // Spectral flux
    if (prevSpectrum) {
      let flux = 0;
      for (let bin = 0; bin < fftSize / 2; bin++) {
        const diff = spectrum[bin] - prevSpectrum[bin];
        if (diff > 0) flux += diff;
      }
      spectralFluxTotal += flux;
    }
    prevSpectrum = spectrum;
  }

  const bassAvg = bassTotal / numWindows;
  const midAvg = midTotal / numWindows;
  const trebleAvg = trebleTotal / numWindows;

  // Per-band scaling factors (calibrated against mastered audio)
  const bassNorm = Math.min(1, bassAvg * 17);
  const midNorm = Math.min(1, midAvg * 250);
  const trebleNorm = Math.min(1, trebleAvg * 210);

  const totalBand = bassAvg + midAvg + trebleAvg || 1;
  const bassRatio = bassAvg / totalBand;
  const trebleRatio = trebleAvg / totalBand;

  const avgFlux = spectralFluxTotal / Math.max(1, numWindows - 1);
  const meanBandAvg = (bassAvg + midAvg + trebleAvg) / 3;
  const percussive = Math.min(1, avgFlux / (Math.max(meanBandAvg, 0.000001) * 30));

  const avgCentroid = spectralCentroidTotal / numWindows;
  const melodic = Math.min(1, (avgCentroid / 3500) * Math.min(1, midNorm + 0.2));

  let rmsSum = 0;
  for (let i = 0; i < data.length; i++) rmsSum += data[i] * data[i];
  const overallNorm = Math.min(1, Math.sqrt(rmsSum / data.length) * 5);

  let density = 'balanced';
  if (bassRatio > 0.55) density = 'bass-heavy';
  else if (trebleRatio > 0.45) density = 'bright';
  else if (percussive > 0.6 && melodic < 0.3) density = 'percussive';
  else if (melodic > 0.5 && percussive < 0.3) density = 'melodic';
  else if (overallNorm > 0.7) density = 'dense';
  else if (overallNorm < 0.2) density = 'sparse';

  return {
    bass: Math.round(bassNorm * 100) / 100,
    mid: Math.round(midNorm * 100) / 100,
    treble: Math.round(trebleNorm * 100) / 100,
    percussive: Math.round(percussive * 100) / 100,
    melodic: Math.round(melodic * 100) / 100,
    overall: Math.round(overallNorm * 100) / 100,
    density
  };
}


// --- Full Analysis Pipeline ---------------------------------------------------

/**
 * Runs the complete analysis pipeline on an audio file.
 */
export async function analyzeLoop(file) {
  const arrayBuffer = await file.arrayBuffer();
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();

  // try/finally guarantees the AudioContext is released even if decodeAudioData
  // rejects (corrupt/unsupported file). Browsers cap ~6 contexts — leaking them
  // on repeated failed uploads would break all audio in the tab.
  let audioBuffer;
  try {
    audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const bpmResult = await detectBPM(audioBuffer);
    const keyResult = await detectKey(audioBuffer);
    const energyResult = await analyzeEnergy(audioBuffer);

    return {
      fileName: file.name,
      duration: audioBuffer.duration,
      bars: bpmResult.bars,
      bpm: bpmResult.bpm,
      bpmConfidence: bpmResult.confidence,
      bpmAlternatives: bpmResult.alternatives,
      key: keyResult.keyString,
      keyNote: keyResult.key,
      keyMode: keyResult.mode,
      keyConfidence: keyResult.confidence,
      chroma: keyResult.chroma,
      energy: energyResult
    };
  } finally {
    audioContext.close();
  }
}
